import React, { useMemo } from 'react'

import { Div } from '@vividseats/vivid-ui-kit'

import CategoryLayout from '@/components/layouts/category-layout'
import { CONCERT_CATEGORY_LINKS, CONCERT_TICKETS_BY_CITY_LINKS } from '@/components/pages/concerts/constants'
import AllProductionListings from '@/components/shared/category/components/all-production-listings'
import CmsContent from '@/components/shared/category/components/cms-content'
import NearbyProductionListings from '@/components/shared/category/components/nearby-production-listings'
import type { CategoryPageProps } from '@/components/shared/category/types'
import FilterBar from '@/components/shared/filter-bar'
import DateFilterWrapper from '@/components/shared/filters/date-filter-wrapper'
import LinkWidget from '@/components/shared/link-widget'
import SortedPerformerLinks from '@/components/shared/performer-links/sorted-performer-links'
import { CATEGORY_IDS } from '@/constants'
import { PerformerProvider } from '@/context/performer'
import { ProductionListProvider } from '@/context/productions-list'
import { PAGE_FILTERS } from '@/context/productions-list/constants'

const ConcertsCategoryContent: React.FC<CategoryPageProps> = ({
    initialAllProductionListParams,
    initialAllProductionListData,
    initialPopularPerformersParams,
    initialPopularPerformersData,
}) => {
    const mainContent: React.ReactNode = useMemo(
        () => (
            <Div marginBottom={11}>
                {/*
                    This ProductionListProvider instance is used only for storing filter parameters, e.g. startDate and endDate
                    So API requests are disabled, i.e. "enabled" is false
                */}
                <ProductionListProvider
                    initialParams={initialAllProductionListParams}
                    initialProps={initialAllProductionListData ? { data: initialAllProductionListData } : undefined}
                    filterMappers={PAGE_FILTERS.categoryPage}
                    enabled={false}
                >
                    <FilterBar>
                        <DateFilterWrapper />
                    </FilterBar>
                    <div data-testid="upcoming-events-near-you">
                        <NearbyProductionListings title="Upcoming Events Near You" categoryId={CATEGORY_IDS.CONCERTS} />
                    </div>
                    <div data-testid="events-in-all-locations">
                        <AllProductionListings title="Events In All Locations" categoryId={CATEGORY_IDS.CONCERTS} />
                    </div>
                </ProductionListProvider>
            </Div>
        ),
        [initialAllProductionListParams, initialAllProductionListData],
    )

    const bottomContent: React.ReactNode = useMemo(
        () => (
            <Div marginTop={11} marginBottom={11}>
                <div data-testid="cms-content">
                    <CmsContent />
                </div>
                <Div display="flex" mobileDisplay="block" tabletJustify="between">
                    <Div marginTop={11} data-testid="popular-concert-tickets">
                        <PerformerProvider
                            initialParams={initialPopularPerformersParams}
                            initialProps={
                                initialPopularPerformersData ? { data: initialPopularPerformersData } : undefined
                            }
                        >
                            <SortedPerformerLinks
                                title="Popular Concert Tickets"
                                variant="LinkWidget"
                                performerNameSuffix=" Tickets"
                            />
                        </PerformerProvider>
                    </Div>
                    <Div marginTop={11} data-testid="concert-categories">
                        <LinkWidget title="Concert Categories" links={CONCERT_CATEGORY_LINKS} />
                    </Div>
                    <Div marginTop={11} data-testid="concert-tickets-by-city">
                        <LinkWidget title="Concert Tickets by City" links={CONCERT_TICKETS_BY_CITY_LINKS} />
                    </Div>
                </Div>
            </Div>
        ),
        [initialPopularPerformersParams, initialPopularPerformersData],
    )

    return <CategoryLayout mainContent={mainContent} bottomContent={bottomContent} />
}

export default ConcertsCategoryContent
